import React, { useState } from 'react';
import styled from 'styled-components';
import { authApi, getApiErrorMessage, saveAuthSession } from "../api";

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  position: relative;
  overflow: hidden;
  background:
    linear-gradient(135deg, rgba(10, 15, 24, 0.82), rgba(59, 43, 11, 0.58)),
    url("/pearl.png");
  background-size: cover;
  background-position: center;
  font-family: Georgia, "Times New Roman", serif;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at top left, rgba(245, 208, 84, 0.18), transparent 26%),
      radial-gradient(circle at right center, rgba(255, 255, 255, 0.08), transparent 20%),
      linear-gradient(180deg, rgba(17, 24, 39, 0.08), rgba(17, 24, 39, 0.52));
    pointer-events: none;
  }

  @media (max-width: 640px) {
    padding: 0;
    align-items: stretch;
  }
`;

const Shell = styled.div`
  position: relative;
  z-index: 1;
  width: min(1200px, 100%);
  min-height: min(820px, calc(100vh - 48px));
  display: grid;
  grid-template-columns: minmax(320px, 460px) minmax(0, 1fr);
  border-radius: 32px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow: 0 30px 90px rgba(15, 23, 42, 0.36);
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(12px);

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    min-height: min(920px, calc(100vh - 32px));
  }

  @media (max-width: 640px) {
    min-height: 100vh;
    border-radius: 0;
    border: 0;
  }
`;

const BrandPanel = styled.section`
  position: relative;
  padding: clamp(24px, 3vw, 42px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;
  color: #ffffff;
  background:
    linear-gradient(180deg, rgba(17, 24, 39, 0.76), rgba(17, 24, 39, 0.48)),
    linear-gradient(135deg, rgba(184, 134, 11, 0.18), transparent 60%);

  &::after {
    content: "";
    position: absolute;
    right: -56px;
    bottom: -56px;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(245, 208, 84, 0.28), transparent 68%);
  }

  @media (max-width: 960px) {
    min-height: 240px;
    padding: 24px 24px 20px;
  }

  @media (max-width: 640px) {
    min-height: 220px;
  }
`;

const BrandTop = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const BrandMark = styled.img`
  width: 72px;
  height: 72px;
  object-fit: contain;
  border-radius: 20px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);

  @media (max-width: 640px) {
    width: 60px;
    height: 60px;
  }
`;

const BrandName = styled.div`
  font-size: 15px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: rgba(245, 208, 84, 0.92);
`;

const BrandBody = styled.div`
  max-width: 420px;
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-size: clamp(2.1rem, 4.5vw, 4.2rem);
  line-height: 0.98;
  letter-spacing: 0.04em;
  color: #ffffff;
`;

const HeroSubtitle = styled.p`
  margin: 14px 0 0;
  font-size: 14px;
  line-height: 1.7;
  color: rgba(243, 244, 246, 0.82);
  max-width: 360px;
`;

const BrandFooter = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  max-width: 360px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  padding: 12px 14px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const StatLabel = styled.div`
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(245, 208, 84, 0.82);
`;

const StatValue = styled.div`
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.55;
  color: #f9fafb;
`;

const FormPanel = styled.section`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(20px, 3vw, 42px);
  background: rgba(252, 250, 247, 0.94);
  overflow: auto;

  @media (max-width: 960px) {
    padding: 24px;
  }

  @media (max-width: 640px) {
    padding: 18px 16px 28px;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 440px;
  padding: clamp(20px, 2.4vw, 32px);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: 0 24px 50px rgba(148, 163, 184, 0.2);

  @media (max-width: 640px) {
    padding: 24px 18px;
    border-radius: 22px;
  }
`;

const Eyebrow = styled.div`
  font-size: 12px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #9a6700;
`;

const Title = styled.h2`
  margin: 10px 0 0;
  color: #111827;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  line-height: 1;
`;

const Subtitle = styled.p`
  margin: 12px 0 0;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.65;
`;

const Form = styled.form`
  margin-top: 20px;
  display: grid;
  gap: 14px;
`;

const Field = styled.div`
  display: grid;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #374151;
`;

const StyledInput = styled.input`
  width: 100%;
  min-height: 50px;
  padding: 0 16px;
  border-radius: 16px;
  border: 1px solid #d1d5db;
  background: #fbfbfa;
  color: #111827;
  font-size: 15px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #b8860b;
    box-shadow: 0 0 0 4px rgba(184, 134, 11, 0.12);
    transform: translateY(-1px);
    background: #ffffff;
  }
`;

const PrimaryButton = styled.button`
  width: 100%;
  min-height: 52px;
  padding: 0 18px;
  border: 0;
  border-radius: 16px;
  background: linear-gradient(135deg, #111827 0%, #b8860b 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
  box-shadow: 0 20px 32px rgba(17, 24, 39, 0.18);
  opacity: ${props => props.disabled ? 0.65 : 1};

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'translateY(-1px)'};
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  min-height: 50px;
  padding: 0 18px;
  border-radius: 16px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    background: #fff8e6;
    border-color: #f3d27a;
  }
`;

const LinkButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: #9a6700;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  text-align: left;

  &:hover {
    color: #7c5300;
  }
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const SuccessMessage = styled.div`
  padding: 13px 14px;
  border-radius: 14px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #15803d;
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
`;

const ErrorMessage = styled.div`
  padding: 13px 14px;
  border-radius: 14px;
  background: #fff1f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
`;

const HintText = styled.p`
  margin: 0;
  color: #6b7280;
  font-size: 13px;
  text-align: center;
  line-height: 1.7;
`;

const DividerText = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #ece7df;
  color: #9a6700;
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const LoginScreen = ({ onLoginSuccess, onSwitchToRegister }) => {
  const [authMode, setAuthMode] = useState('login');
  const [formData, setFormData] = useState({ identifier: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [forceResetEmail, setForceResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwChangeLoading, setPwChangeLoading] = useState(false);
  const [pwChangeError, setPwChangeError] = useState('');
  const [pwChangeSuccess, setPwChangeSuccess] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotResetToken, setForgotResetToken] = useState('');
  const [forgotNewPassword, setForgotNewPassword] = useState('');
  const [forgotConfirmPassword, setForgotConfirmPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await authApi.post("/login", {
        email: formData.identifier,
        password: formData.password,
      });

      if (res.data.force_password_reset) {
        setForceResetEmail(res.data.email || formData.identifier);
        setAuthMode('force-reset');
        setLoading(false);
        return;
      }

      saveAuthSession({
        accessToken: res.data.access_token,
        refreshToken: res.data.refresh_token,
        user: res.data.user,
      });

      onLoginSuccess(res.data.user);
    } catch (error) {
      console.error("Login failed:", error.response?.data || error.message);
      alert(getApiErrorMessage(error, "Invalid credentials"));
    } finally {
      setLoading(false);
    }
  };

  const resetForgotState = () => {
    setForgotOtp('');
    setForgotResetToken('');
    setForgotNewPassword('');
    setForgotConfirmPassword('');
    setForgotLoading(false);
    setForgotError('');
    setForgotSuccess('');
  };

  const openForgotPassword = () => {
    resetForgotState();
    setForgotEmail((formData.identifier || '').trim());
    setAuthMode('forgot-email');
  };

  const backToLogin = () => {
    setAuthMode('login');
    setPwChangeError('');
    setPwChangeSuccess(false);
    resetForgotState();
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwChangeError('');

    if (newPassword.length < 6) {
      setPwChangeError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwChangeError('Passwords do not match.');
      return;
    }

    setPwChangeLoading(true);
    try {
      await authApi.post("/reset-password", {
        email: forceResetEmail,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setPwChangeSuccess(true);
      setTimeout(() => {
        setAuthMode('login');
        setPwChangeSuccess(false);
        setNewPassword('');
        setConfirmPassword('');
        setFormData({ identifier: forceResetEmail, password: '' });
      }, 2000);
    } catch (error) {
      setPwChangeError(getApiErrorMessage(error, 'Failed to change password. Please try again.'));
    } finally {
      setPwChangeLoading(false);
    }
  };

  const handleForgotRequestOtp = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');

    if (!forgotEmail.trim()) {
      setForgotError('Please enter your email address.');
      return;
    }

    setForgotLoading(true);
    try {
      const res = await authApi.post('/auth/forgot-password/request', {
        email: forgotEmail.trim(),
      });
      setForgotSuccess(res.data?.message || 'OTP sent to your email.');
      setForgotOtp('');
      setAuthMode('forgot-otp');
    } catch (error) {
      setForgotError(getApiErrorMessage(error, 'Failed to send OTP.'));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotVerifyOtp = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');

    if (!forgotOtp.trim() || forgotOtp.trim().length !== 4) {
      setForgotError('Please enter the 4 digit OTP.');
      return;
    }

    setForgotLoading(true);
    try {
      const res = await authApi.post('/auth/forgot-password/verify', {
        email: forgotEmail.trim(),
        otp_code: forgotOtp.trim(),
      });
      setForgotResetToken(res.data?.reset_token || '');
      setForgotSuccess('OTP verified. You can now set a new password.');
      setAuthMode('forgot-reset');
    } catch (error) {
      setForgotError(getApiErrorMessage(error, 'Failed to verify OTP.'));
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotResetPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');

    if (forgotNewPassword.length < 6) {
      setForgotError('Password must be at least 6 characters.');
      return;
    }
    if (forgotNewPassword !== forgotConfirmPassword) {
      setForgotError('Passwords do not match.');
      return;
    }

    setForgotLoading(true);
    try {
      await authApi.post('/auth/forgot-password/reset', {
        email: forgotEmail.trim(),
        reset_token: forgotResetToken,
        new_password: forgotNewPassword,
        confirm_password: forgotConfirmPassword,
      });
      setForgotSuccess('Password changed successfully. Please sign in with your new password.');
      setFormData({ identifier: forgotEmail.trim(), password: '' });
      setTimeout(() => {
        backToLogin();
      }, 1500);
    } catch (error) {
      setForgotError(getApiErrorMessage(error, 'Failed to reset password.'));
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <PageWrapper>
      <Shell>
        <BrandPanel>
          <BrandTop>
            <BrandMark src="/emblem.png" alt="Zilverra emblem" />
            <BrandName>Zilverra</BrandName>
          </BrandTop>

          <BrandBody>
            <HeroTitle>The Gold Standard</HeroTitle>
            <HeroSubtitle>
              A refined operating space for jewellery teams across production, inventory, sales, quality, HR, and accounts.
            </HeroSubtitle>
          </BrandBody>

          <BrandFooter>
            <StatCard>
              <StatLabel>Precision</StatLabel>
              <StatValue>Track every piece, process, and promise with clarity.</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Control</StatLabel>
              <StatValue>Move from login to daily work on any screen size, especially mobile.</StatValue>
            </StatCard>
          </BrandFooter>
        </BrandPanel>

        <FormPanel>
          {authMode === 'force-reset' ? (
            <Card>
              <Eyebrow>Secure Access</Eyebrow>
              <Title>Set New Password</Title>
              <Subtitle>Your account requires a password change before you can sign in.</Subtitle>

              {pwChangeSuccess ? (
                <SuccessMessage>
                  Password changed successfully. Redirecting you back to login.
                </SuccessMessage>
              ) : (
                <Form onSubmit={handlePasswordChange}>
                  <Field>
                    <Label>New Password</Label>
                    <StyledInput
                      type="password"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                  </Field>

                  <Field>
                    <Label>Confirm Password</Label>
                    <StyledInput
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </Field>

                  {pwChangeError ? <ErrorMessage>{pwChangeError}</ErrorMessage> : null}

                  <PrimaryButton type="submit" disabled={pwChangeLoading}>
                    {pwChangeLoading ? 'Saving...' : 'Set Password'}
                  </PrimaryButton>

                  <SecondaryButton type="button" onClick={backToLogin}>
                    Back to Login
                  </SecondaryButton>
                </Form>
              )}
            </Card>
          ) : authMode === 'forgot-email' ? (
            <Card>
              <Eyebrow>Password Recovery</Eyebrow>
              <Title>Forgot Password</Title>
              <Subtitle>Enter your email address. We will send a 4 digit OTP that expires in 5 minutes.</Subtitle>

              <Form onSubmit={handleForgotRequestOtp}>
                <Field>
                  <Label>Email Address</Label>
                  <StyledInput
                    type="email"
                    placeholder="name@company.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                  />
                </Field>

                {forgotError ? <ErrorMessage>{forgotError}</ErrorMessage> : null}
                {forgotSuccess ? <SuccessMessage>{forgotSuccess}</SuccessMessage> : null}

                <PrimaryButton type="submit" disabled={forgotLoading}>
                  {forgotLoading ? 'Sending OTP...' : 'Send OTP'}
                </PrimaryButton>

                <SecondaryButton type="button" onClick={backToLogin}>
                  Back to Login
                </SecondaryButton>
              </Form>
            </Card>
          ) : authMode === 'forgot-otp' ? (
            <Card>
              <Eyebrow>Password Recovery</Eyebrow>
              <Title>Verify OTP</Title>
              <Subtitle>Enter the 4 digit OTP sent to {forgotEmail || 'your email'}. It expires in 5 minutes.</Subtitle>

              <Form onSubmit={handleForgotVerifyOtp}>
                <Field>
                  <Label>Email Address</Label>
                  <StyledInput
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                  />
                </Field>

                <Field>
                  <Label>4 Digit OTP</Label>
                  <StyledInput
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="Enter OTP"
                    value={forgotOtp}
                    onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    required
                  />
                </Field>

                {forgotError ? <ErrorMessage>{forgotError}</ErrorMessage> : null}
                {forgotSuccess ? <SuccessMessage>{forgotSuccess}</SuccessMessage> : null}

                <PrimaryButton type="submit" disabled={forgotLoading}>
                  {forgotLoading ? 'Verifying...' : 'Verify OTP'}
                </PrimaryButton>

                <ActionRow>
                  <LinkButton type="button" onClick={handleForgotRequestOtp} disabled={forgotLoading}>
                    Resend OTP
                  </LinkButton>
                  <LinkButton type="button" onClick={backToLogin}>
                    Back to Login
                  </LinkButton>
                </ActionRow>
              </Form>
            </Card>
          ) : authMode === 'forgot-reset' ? (
            <Card>
              <Eyebrow>Password Recovery</Eyebrow>
              <Title>Set New Password</Title>
              <Subtitle>OTP verified for {forgotEmail}. Enter your new password and confirm it.</Subtitle>

              <Form onSubmit={handleForgotResetPassword}>
                <Field>
                  <Label>New Password</Label>
                  <StyledInput
                    type="password"
                    placeholder="Enter new password"
                    value={forgotNewPassword}
                    onChange={(e) => setForgotNewPassword(e.target.value)}
                    required
                  />
                </Field>

                <Field>
                  <Label>Re-enter Password</Label>
                  <StyledInput
                    type="password"
                    placeholder="Re-enter new password"
                    value={forgotConfirmPassword}
                    onChange={(e) => setForgotConfirmPassword(e.target.value)}
                    required
                  />
                </Field>

                {forgotError ? <ErrorMessage>{forgotError}</ErrorMessage> : null}
                {forgotSuccess ? <SuccessMessage>{forgotSuccess}</SuccessMessage> : null}

                <PrimaryButton type="submit" disabled={forgotLoading}>
                  {forgotLoading ? 'Updating...' : 'OK'}
                </PrimaryButton>

                <SecondaryButton type="button" onClick={backToLogin}>
                  Back to Login
                </SecondaryButton>
              </Form>
            </Card>
          ) : (
            <Card>
              <Eyebrow>Welcome Back</Eyebrow>
              <Title>Sign In</Title>
              <Subtitle>Access your workspace with the email and password assigned to your account.</Subtitle>

              <Form onSubmit={handleLogin}>
                <Field>
                  <Label>Email Address</Label>
                  <StyledInput
                    type="email"
                    placeholder="name@company.com"
                    value={formData.identifier}
                    onChange={(e) => setFormData({ ...formData, identifier: e.target.value })}
                    required
                  />
                </Field>

                <Field>
                  <Label>Password</Label>
                  <StyledInput
                    type="password"
                    placeholder="Enter password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                </Field>

                <ActionRow>
                  <HintText style={{ margin: 0, textAlign: 'left' }}>Use your assigned company credentials.</HintText>
                  <LinkButton type="button" onClick={openForgotPassword}>
                    Forgot Password?
                  </LinkButton>
                </ActionRow>

                <PrimaryButton type="submit" disabled={loading}>
                  {loading ? 'Authenticating...' : 'Sign In'}
                </PrimaryButton>
              </Form>

              {false && (
                <>
                  <DividerText>New to Zilverra</DividerText>
                  <HintText>Set up your organization, verify the admin email, and start from a polished mobile-ready workspace.</HintText>
                  <div style={{ marginTop: '16px' }}>
                    <SecondaryButton type="button" onClick={onSwitchToRegister}>
                      Create New Organization
                    </SecondaryButton>
                  </div>
                </>
              )}
            </Card>
          )}
        </FormPanel>
      </Shell>
    </PageWrapper>
  );
};

export default LoginScreen;
